import React, { useEffect, useState } from 'react';

import PageLayout from 'components/layouts/PageLayout';
import NewButton from 'components/input/NewButton';
import styled from 'styled-components';
import Text from 'components/commons/Text';
import { useNavigate } from 'react-router-dom';
import { Row } from 'saladsoft-primitive';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ServiceHelper from 'stores/ServiceHelper';
import { AdminListingDto } from 'services/data-contracts';
import InputField from 'components/input/InputField';
import { IconButton } from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import GridItem from 'components/pages/listings/GridItem';
import ListItem from 'components/pages/listings/ListItem';
import SequenceItem from 'components/pages/listings/SequenceItem';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

const ListingsPage = () => {
  const navigate = useNavigate();
  const [showScroll, setShowScroll] = useState(false);

  const [filteredListings, setFilteredListings] = useState<AdminListingDto[]>([]);
  const [listings, setListings] = useState<AdminListingDto[]>([]);
  const [listingType, setListingType] = useState<'grid' | 'sequence' | 'list'>('list');

  // DnD 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const PageTitle = () => {
    return (
      <Row gap="10px" padding="0 20px">
        <Text type="H2">전체 시설목록</Text>
        <Text type="H4">{listings?.length}개의 시설이 등록되어 있습니다.</Text>
      </Row>
    );
  };

  const getListings = async () => {
    const result = await ServiceHelper?.adminListingService?.getListings1({
      size: 1000,
      sort: ['seq,asc'],
    });
    if (result?.status === 200) {
      const sortedListings = result?.data.sort((a, b) => {
        if (!a.seq) return 1;
        if (!b.seq) return -1;
        return Number(a.seq) - Number(b.seq);
      });
      setListings(sortedListings);
      setFilteredListings(sortedListings);
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const checkScrollTop = () => {
    if (window.scrollY > 200) {
      setShowScroll(true);
    } else {
      setShowScroll(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    console.log(event);

    if (over && active.id !== over.id) {
      const updatedListings = arrayMove(
        filteredListings, // 또는 items 등 현재 배열
        filteredListings.findIndex((item) => item.id?.toString() === active.id),
        filteredListings.findIndex((item) => item.id?.toString() === over.id),
      );

      const reorderIds = updatedListings
        .map((item) => item.id)
        .filter((id) => id !== undefined) as number[];
      ServiceHelper?.adminListingService?.reorderListings(reorderIds);
      setFilteredListings(updatedListings);
    }
  };

  const updateSequence = async () => {
    try {
      // Create a copy of filtered listings with updated sequence numbers
      const updatedListings = filteredListings.map((listing, index) => ({
        ...listing,
        seq: index + 1,
      }));

      console.log(updatedListings);

      // Update the state with the new sequence
      // setFilteredListings(updatedListings);

      // Create an array of promises for updating each listing
      // const updatePromises = updatedListings.map((listing) => {
      //   if (listing.id) {
      //     return saveListing(listing);
      //   }
      //   return Promise.resolve();
      // });

      // Wait for all updates to complete
      // await Promise.all(updatePromises);

      // Refresh the listings to get the latest order from server
      // getListings();
    } catch (error) {
      console.error('Failed to update sequence:', error);
    }
  };

  useEffect(() => {
    getListings();
    window.addEventListener('scroll', checkScrollTop);
    return () => window.removeEventListener('scroll', checkScrollTop);
  }, []);

  return (
    <PageLayout
      style={{ padding: 0 }}
      title={<PageTitle />}
      actionButton={
        <Row gap="10px" padding="0 20px">
          <Row gap="10px" marginRight="20px">
            <IconButton
              style={{ background: listingType === 'list' ? '#efefef' : 'transparent' }}
              onClick={() => setListingType('list')}
            >
              <FormatListBulletedIcon />
            </IconButton>
            <IconButton
              style={{ background: listingType === 'grid' ? '#efefef' : 'transparent' }}
              onClick={() => setListingType('grid')}
            >
              <BrokenImageOutlinedIcon />
            </IconButton>
            <IconButton
              style={{ background: listingType === 'sequence' ? '#efefef' : 'transparent' }}
              onClick={() => setListingType('sequence')}
            >
              <LowPriorityIcon />
            </IconButton>
          </Row>
          <NewButton
            onClick={() => {
              navigate('/admin/listings/new');
            }}
          >
            시설추가
          </NewButton>
        </Row>
      }
    >
      <Row
        maxWidth="100%"
        position="sticky"
        top="50px"
        zIndex={100}
        background="#fff"
        borderRadius="10px"
        padding="0 20px"
      >
        <InputField
          placeholder="시설 찾기"
          onChange={(value: string) => {
            setFilteredListings(
              listings.filter((listing) => listing.localizedName?.['ko']?.includes(value)),
            );
          }}
        />
      </Row>
      {listingType === 'sequence' ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={filteredListings.map((item) => item.id?.toString() || '')}
            strategy={rectSortingStrategy}
          >
            <SequenceContainer>
              {filteredListings?.map((listing) => (
                <SequenceItem
                  key={listing.id}
                  listing={listing}
                  id={listing.id?.toString() || ''}
                />
              ))}
            </SequenceContainer>
          </SortableContext>
        </DndContext>
      ) : (
        <Container listingType={listingType}>
          {filteredListings?.map((listing, index) => (
            <>
              {listingType === 'grid' ? (
                <GridItem key={index} listing={listing} />
              ) : (
                <ListItem key={index} listing={listing} />
              )}
            </>
          ))}
        </Container>
      )}
      <UpContainer showScroll={showScroll}>
        <IconButton
          style={{
            background: '#efefef',
            zIndex: 1000,
            width: '70px',
            height: '70px',
          }}
          onClick={handleScrollToTop}
        >
          <ArrowUpwardOutlinedIcon />
        </IconButton>
      </UpContainer>
    </PageLayout>
  );
};

const Container = styled.div<{ listingType: 'grid' | 'sequence' | 'list' }>`
  display: ${(props) =>
    props.listingType === 'grid' || props.listingType === 'sequence' ? 'flex' : 'block'};
  flex-wrap: ${(props) =>
    props.listingType === 'grid' || props.listingType === 'sequence' ? 'wrap' : 'nowrap'};
  gap: 20px;
  padding: 0 20px;
  min-height: ${(props) => (props.listingType === 'sequence' ? '100vh' : 'auto')};
  align-items: flex-start;
  align-content: flex-start;

  margin-top: 20px;
  > div {
    ${(props) =>
      props.listingType === 'grid'
        ? `
          flex: 1 1 350px;
          min-width: 0;
          max-width: 400px;
          width: calc((100% - 40px) / 3);

          @media (max-width: 1200px) {
            width: calc((100% - 20px) / 2);
          }

          @media (max-width: 768px) {
            width: 100%;
          }
        `
        : props.listingType === 'sequence'
          ? `
          flex: 0 0 auto;
          width: 200px;
          margin-right: 10px;
          margin-bottom: 10px;
        `
          : `
          flex: 0 0 auto;
          width: 100%;
        `}
  }
`;

const SequenceContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px;
  margin-top: 20px;
`;

const UpContainer = styled.div<{ showScroll: boolean }>`
  position: fixed;
  bottom: ${(props) => (props.showScroll ? '50px' : '-100px')};
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  transition: bottom 0.3s ease-in-out;
`;

export default ListingsPage;
